<svelte:options accessors />

<script lang="ts">
  import {project, templates} from '@/store';
  import type {GridConfig, PreviewImage} from '@/types/common';
  import type {Shapes} from '@/types/filters';
  import Masonry from 'masonry-layout';
  import {afterUpdate, beforeUpdate, createEventDispatcher, onMount, tick} from 'svelte';
  import Swal from 'sweetalert2';
  import {TemplatesService} from '../../services';
  import Icon from '../icon/Icon.svelte';
  import SkeletonLoader from '../skeleton-loader/SkeletonLoader.svelte';
  import Image from './Image.svelte';
  import {sizes} from './sizes';

  let initState: PreviewImage = {
    id: 0,
    src: '',
    title: '',
    content: '',
    hasHover: false,
    devalueLevel: 0,
    isFeatured: false,
    isFree: false,
    shape: ''
  };

  //TODO: change type on API update
  export let props: GridConfig;
  export let selected = initState;
  export let loadMore: Boolean;
  export let selectedTags: string;
  export let selectedShape: Shapes = '';
  export let page: number;
  export let limit: number;
  export let favorites: boolean;

  let shapes = sizes;

  const templateService = new TemplatesService();

  let loadingElem: HTMLDivElement | null = null;
  let containerElem: HTMLDivElement;
  let subItems: HTMLElement | null = null;
  let masonry: Masonry;
  let currentTemplates = 0;
  let __slicedTemplates: PreviewImage[] = [];
  let promise: any;

  const dispatch = createEventDispatcher();

  const selectImage = (image: PreviewImage) => {
    selected = image;
    dispatch('on-image-click', {
      disabled: false,
      image: selected
    });
  };

  const deselectAllImages = () => {
    selected = initState;
    dispatch('on-image-click', {
      disabled: true
    });
  };

  const hideGrid = () => {
    if (!containerElem) return console.log('containerElem is null');
    containerElem.classList.add('hidden');
  };

  const showGrid = () => {
    if (!containerElem) return console.log('containerElem is null');
    containerElem.classList.remove('hidden');
  };

  beforeUpdate(() => {
    if (loadingElem !== null && containerElem !== null) {
      hideGrid();
    }
  });

  afterUpdate(async () => {
    if (!containerElem) return console.log('containerElem is null');
    await tick();
    masonry = new Masonry(containerElem, {
      itemSelector: '.image-item',
      columnWidth: '.image-sizer',
      percentPosition: true
    });
    showGrid();
  });

  onMount(() => {
    subItems = document.querySelector('#template-blocks');

    // if ($project.template !== null) {
    //   dispatch('on-image-click', {
    //     disabled: false,
    //     image: {
    //       hasHover: false,
    //       id: $project.template.id,
    //       content: $project.template.content,
    //       devalueLevel: $project.template.devalueLevel,
    //       shape: $project.template.shape,
    //       isFree: $project.template.isFree,
    //       isFavorite: $project.template.isFavorite,
    //       src: $project.template.preview,
    //       title: $project.template.title,
    //       isFeatured: $project.template.isFeatured
    //     }
    //   });
    // }

    promise = Promise.resolve(appendTemplates());
  });

  const loadMasonry = () => {
    if (masonry.layout) {
      masonry.layout();
    }

    if (props.loading === false && !!loadingElem && !!containerElem) {
      containerElem.classList.remove('hidden');
    }
  };

  const appendTemplates = async (loadMore = true) => {
    if (loadMore) {
      currentTemplates = currentTemplates + 16;

      dispatch('append-templates', {
        currentPage: page
      });
    }

    __slicedTemplates = props.images.slice(0, currentTemplates).map(item => {
      return item;
    });

    return new Promise(resolve => {
      setTimeout(() => {
        isLoading = false;
        resolve(__slicedTemplates);
      }, 3000);
    });
  };

  let isLoading = false;

  $: if (props) {
    isLoading = true;
    appendTemplates(false);
  }

  $: if (loadMore) {
    isLoading = true;
    page = page + 1;
    appendTemplates();
  }

  const buttonClass =
    'flex items-center justify-center cursor-pointer transition-all duration-150 py-2 px-4 rounded border-2';

  const deleteTemplate = (item: PreviewImage) => {
    Swal.fire({
      showDenyButton: true,
      buttonsStyling: false,
      showCloseButton: true,
      title: 'Are you sure you want to delete this template?',
      denyButtonText: `No do not delete`,
      confirmButtonText: 'Yes PERMANENTLY Delete this template',
      text: `You are about to permanently delete this template`,
      customClass: {
        actions: 'flex gap-4',
        title: '!text-left border border-b border-gray-200',
        confirmButton: `${buttonClass} border-primary hover:bg-primary text-primary hover:text-white`,
        denyButton: `${buttonClass} border-green-500 bg-green-500 hover:bg-green-600 hover:border-green-600 text-white`
      },
      allowOutsideClick: () => !Swal.isLoading()
    }).then(async result => {
      if (result.isConfirmed) {
        const result = await templateService.deleteTemplate(item.id);
        if (result) {
          Swal.fire({
            position: 'bottom-end',
            icon: 'success',
            title: 'Template Deleted',
            showConfirmButton: false,
            timer: 1500,
            toast: true
          });
          templates.getTemplates(page, limit, selectedShape, selectedTags, favorites, true);
        }
        return true;
      }
      return false;
    });
  };

  const featureTemplate = async (item: PreviewImage, index: number) => {
    let featured = item.isFeatured;
    const result = await templateService.featureTemplate(item.id, !featured);

    if (result) {
      Swal.fire({
        position: 'bottom-end',
        icon: 'success',
        title: 'Template Updated',
        showConfirmButton: false,
        timer: 1500,
        toast: true
      });
      props.images[index].isFeatured = !item.isFeatured;
    }
  };

  const setFavorite = async (item: PreviewImage, index: number) => {
    const result = await templateService.setFavorite(item.id);
    if (result) {
      Swal.fire({
        position: 'bottom-end',
        icon: 'success',
        title: 'Template Favorites Updated',
        showConfirmButton: false,
        timer: 1500,
        toast: true
      });
      props.images[index].isFavorite = !item.isFavorite;
    }
  };

  const devalueTemplate = async (item: PreviewImage, value: number) => {
    try {
      const newDevalueLevel = (item.devalueLevel ?? 0) + value;
      const result = await templateService.setDevalue(item.id, newDevalueLevel);
      if (!result) throw 'error';

      item.devalueLevel = newDevalueLevel;
      Swal.fire({
        position: 'bottom-end',
        icon: 'success',
        title: 'Template successfully devalued',
        showConfirmButton: false,
        timer: 1500,
        toast: true
      });

      page = 1;
      appendTemplates();
    } catch (error) {
      Swal.fire({
        position: 'bottom-end',
        icon: 'error',
        title: 'Template failed to devalue',
        showConfirmButton: false,
        timer: 1500,
        toast: true
      });
    }
  };
</script>

<div class="grid-containerElem flex flex-col gap-4 p-8">
  {#await promise}
    <div class="h-auto w-full" bind:this={containerElem}>
      <div class="image-sizer" />
      {#each __slicedTemplates as item}
        <SkeletonLoader step="choose-template" {item} />
      {/each}
    </div>
  {:then}
    {#if props.loading === true}
      <div class="grid h-[28rem] w-full grid-cols-4">
        {#each Array(4) as item}
          <SkeletonLoader step="choose-template" {item} />
        {/each}
      </div>
    {:else if props.images.length <= 0}
      <div class="flex w-full justify-center py-2">
        <h1 class="text-4xl font-bold">No Templates Found.</h1>
      </div>
    {:else}
      <div id="template-blocks" class="h-auto w-full" bind:this={containerElem}>
        <div class="image-sizer" />
        <div class="image-item relative flex w-1/4 cursor-pointer flex-col gap-2 text-ellipsis p-2">
          <div class="ratio169 border-1 flex flex-col gap-3 rounded-xl border border-zinc-200 px-2 py-6">
            <span class="text-md flex justify-center">Create From Scratch</span>
            <div class="flex w-full flex-row justify-center gap-4">
              {#each shapes as dimension}
                <div class="" on:click={() => selectImage(dimension)} on:keypress={() => selectImage(dimension)}>
                  <div class="flex cursor-pointer flex-col justify-center gap-1">
                    <div
                      class={`icon-container  ${
                        selected.shape === dimension.shape
                          ? 'bg-primary text-white '
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-400 hover:text-white'
                      } flex items-center justify-center`}
                    >
                      <Icon name={dimension.shape} className={`w-10 h-10 `} />
                    </div>
                  </div>

                  <!-- <input
                      bind:group={dim}
                      id={dimension.shape}
                      type="radio"
                      value={dimension.shape}
                      class="hidden"
                      name="filter-size"
                      on:click={()=>{console.log(dimension.shape, shape, dim)}}
                    /> -->
                  <p class="mt-2 text-center text-xs">{dimension.shape}</p>
                </div>
              {/each}
            </div>
          </div>
        </div>

        {#each __slicedTemplates as item, i (i)}
          <Image
            {item}
            {selected}
            on:image-load={loadMasonry}
            on:image-click={() => selectImage(item)}
            on:out-click={() => deselectAllImages()}
            on:admin-click={() => deleteTemplate(item)}
            on:feature-click={() => featureTemplate(item, i)}
            on:set-favorite={() => setFavorite(item, i)}
            on:devalue-up={() => devalueTemplate(item, 1)}
            on:devalue-down={() => devalueTemplate(item, -1)}
          />
        {/each}
      </div>
      {#if isLoading}
        <div class="grid h-[28rem] w-full grid-cols-4">
          {#each Array(4) as item}
            <SkeletonLoader step="choose-template" {item} />
          {/each}
        </div>
      {/if}
    {/if}
  {/await}
</div>

<style lang="postcss">
  .image-sizer {
    @apply w-1/4;
  }

  .icon-container {
    @apply flex h-14 w-14 items-center justify-center rounded shadow transition-all;
  }
  .ratio169 {
    padding-top: 64.5%;
    padding-bottom: 64.5%;
  }
</style>
